import { Modal } from '@unified-checkout/design-system';
import { useForm } from 'react-hook-form';
import { OneClickPhoneInput } from '../OneClickPhoneInput/OneClickPhoneInput';
import { useEffect, useState } from 'react';
import { OnResendProps } from './ClickOTPModal';
import { useTranslation } from 'react-i18next';
import { MobileModal } from './MobileModal';
import { useWidth } from '../../hooks/useWidth';
import { TCountry } from '../../utils/getDomainCountry';

type ClickPhoneVerificationModalProps = {
  isOpen: boolean;
  onVerify: (props: OnResendProps) => Promise<void>;
  onCancel: () => void;
  phoneHint: string;
  OTPError: string;
  country: TCountry;
  setCountry: React.Dispatch<React.SetStateAction<TCountry>>;
  setCurrentPhone?: React.Dispatch<React.SetStateAction<string | undefined>>;
};
export const ClickPhoneVerificationModal = ({
  isOpen,
  onVerify,
  onCancel,
  phoneHint,
  OTPError,
  country,
  setCountry,
  setCurrentPhone
}: ClickPhoneVerificationModalProps) => {
  const isMobile = useWidth();
  const { t } = useTranslation('kitComponents');

  const {
    register,
    setValue,
    clearErrors,
    formState: { errors, isValid, dirtyFields },
    getValues,
    trigger,
  } = useForm<{ phone_number: string }>({
    mode: 'onBlur',
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    isValid && isMobile && handleVerify();
  }, [isValid]);

  useEffect(() => {
    if (dirtyFields.phone_number || getValues('phone_number')) {
      trigger('phone_number');
    }
  }, [country?.code]);

  useEffect(() => {
    setCurrentPhone?.(getValues('phone_number'));  
  },[getValues('phone_number')])

  const handleVerify = async () => {
    setIsLoading(true);
    await onVerify({
      sendTo: 'email',
      phone: getValues('phone_number'),
    });
    setIsLoading(false);
  };
  if (isMobile && isOpen) {
    return (
      <MobileModal
        title={t('VERIFY_TITLE')}
        text={t('VERIFY_DESCRIPTION') + (phoneHint ? t('VERIFY_DESCRIPTION_2') + phoneHint : '')}
        onCancel={onCancel}
        hasBackButton
      >
        <div className="w-full mx-auto">
          <OneClickPhoneInput
            clearErrors={clearErrors}
            errors={errors || OTPError}
            register={register}
            setValue={setValue}
            country={country}
            setCountry={setCountry}
          />
          {OTPError && <p className="mt-1 text-sm text-red-500">{OTPError}</p>}
        </div>
      </MobileModal>
    );
  }

  return (
    <Modal
      width="25rem"
      open={isOpen}
      title={t('VERIFY_TITLE')}
      supportingText={t('VERIFY_DESCRIPTION') + (phoneHint ? t('VERIFY_DESCRIPTION_2') + phoneHint : '')}
      btnsProps={[
        {
          color: 'primary',
          text: t('VERIFY'),
          onClickHandler: handleVerify,
          disabled: !isValid || isLoading,
          loading: isLoading,
        },
        {
          color: 'secondary',
          variant: 'outlined',
          text: t('CANCEL_BTN'),
          onClickHandler: onCancel,
        },
      ]}
      position='center'
    >
      <div className="mx-auto my-6 w-[21.5rem]">
        <OneClickPhoneInput
          clearErrors={clearErrors}
          errors={errors}
          register={register}
          setValue={setValue}
          country={country}
          setCountry={setCountry}
        />
        {OTPError && <p className="mt-1 text-sm text-red-500">{OTPError}</p>}
      </div>
    </Modal>
  );
};
